import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Clock, Calendar, Users, CheckCircle, AlertCircle, Timer,
  ChevronLeft, ChevronRight, Download, Filter, Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Helper function to format date as YYYY-MM-DD in local timezone (not UTC)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Attendance = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [myHistory, setMyHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { getAuthHeaders, isOfficeAdmin, isSuperAdmin, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isAdmin = isOfficeAdmin() || isSuperAdmin();

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const dateStr = formatLocalDate(selectedDate);

      if (isAdmin) {
        // Fetch office summary and records
        const [summaryRes, recordsRes] = await Promise.all([
          fetch(`${API_URL}/api/attendance/office-summary?target_date=${dateStr}`, { headers }),
          fetch(`${API_URL}/api/attendance/office-records?target_date=${dateStr}`, { headers })
        ]);

        if (summaryRes.ok) {
          setSummary(await summaryRes.json());
        }
        if (recordsRes.ok) {
          setRecords(await recordsRes.json());
        }
      }

      // Fetch personal history
      const historyRes = await fetch(`${API_URL}/api/attendance/my-history?limit=30`, { headers });
      if (historyRes.ok) {
        setMyHistory(await historyRes.json());
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime, formattedTime) => {
    // Use pre-formatted time from backend if available
    if (formattedTime) return formattedTime;
    if (!datetime) return '-';
    // Fallback to local formatting
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatHours = (hours) => {
    if (!hours) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusBadge = (record) => {
    if (record.check_out_time) {
      return <Badge className="bg-gray-100 text-gray-800">Completed</Badge>;
    }
    if (record.is_late) {
      return <Badge className="bg-yellow-100 text-yellow-800">Late</Badge>;
    }
    if (record.check_in_time) {
      return <Badge className="bg-green-100 text-green-800">Present</Badge>;
    }
    return <Badge className="bg-red-100 text-red-800">Absent</Badge>;
  };

  const navigateDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Attendance</h1>
          <p className="text-gray-600 dark:text-gray-400">Track employee attendance and time</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={goToToday}>
            Today
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Admin Stats */}
      {isAdmin && summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Present Today</p>
                  <p className="text-2xl font-bold text-green-600">{summary.present}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Late Arrivals</p>
                  <p className="text-2xl font-bold text-yellow-600">{summary.late}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Absent</p>
                  <p className="text-2xl font-bold text-red-600">{summary.absent}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">On Leave</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.on_leave}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Staff</p>
                  <p className="text-2xl font-bold">{summary.total_employees}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Date Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-4">
              <Calendar className="h-5 w-5 text-gray-500" />
              <span className="font-semibold">{formatDate(selectedDate)}</span>
            </div>
            <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin: Office Attendance Table */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Daily Attendance</CardTitle>
              <Badge variant="outline">{records.length} records</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {records.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Employee</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Check In</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Check Out</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Hours</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => (
                      <tr key={record.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {record.employee_name?.split(' ').map(n => n[0]).join('') || '?'}
                            </div>
                            <span className="font-medium">{record.employee_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={!record.check_in_time ? 'text-gray-400' : ''}>
                            {formatTime(record.check_in_time, record.check_in_formatted)}
                          </span>
                          {record.is_late && record.late_minutes && (
                            <span className="ml-2 text-xs text-yellow-600">
                              (+{record.late_minutes}m late)
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={!record.check_out_time ? 'text-gray-400' : ''}>
                            {formatTime(record.check_out_time, record.check_out_formatted)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={!record.total_hours ? 'text-gray-400' : 'font-medium'}>
                            {formatHours(record.total_hours)}
                          </span>
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(record)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No attendance records for this date</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* My Attendance History */}
      <Card>
        <CardHeader>
          <CardTitle>My Attendance History</CardTitle>
        </CardHeader>
        <CardContent>
          {myHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Check In</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Check Out</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Hours</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myHistory.map((record) => (
                    <tr key={record.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-3 px-4 font-medium">
                        {new Date(record.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <span className={!record.check_in_time ? 'text-gray-400' : ''}>
                          {formatTime(record.check_in_time, record.check_in_formatted)}
                        </span>
                        {record.is_late && (
                          <Badge className="ml-2 bg-yellow-100 text-yellow-800 text-xs">Late</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={!record.check_out_time ? 'text-gray-400' : ''}>
                          {formatTime(record.check_out_time, record.check_out_formatted)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={!record.total_hours ? 'text-gray-400' : 'font-medium'}>
                          {formatHours(record.total_hours)}
                        </span>
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(record)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Timer className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No attendance history yet</p>
              <p className="text-sm mt-1">Use the Check In button in the header to record your attendance</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Attendance;
